import React, { useCallback } from 'react';

import {
  IconButton,
  Card,
  CardHeader,
  CardContent,
  Stack,
  DescriptionList,
  DescriptionItem,
  View,
  EditIcon,
  CloseIcon,
} from '@gogame/components';
import { useMountedState } from '@gogame/hooks-ts';
import { useCurrencySymbol, DateRangeFormatter } from '@gogame/intl-ts';

import {
  APPLICATION,
  COUNTRY,
  NETWORK,
  PLATFORM,
  ADJUSTMENT_REASON,
} from 'scenes/SpendAdjustments/components/SpendAdjustmentForm/constants';
import Status from 'scenes/SpendAdjustments/components/Status';
import {
  loadApplications,
  loadCountries,
  loadNetworks,
  loadPlatforms,
} from 'scenes/SpendAdjustments/sources/fields';

export const SpendAdjustmentCard = ({
  index,
  onRemove,
  handleEditSpendAdjustment,
  isEdit,
  model,
}) => {
  const { period, currency } = model;
  const handleEdit = () => {
    handleEditSpendAdjustment(index);
  };

  const handleRemove = useCallback(() => onRemove(index), [index, onRemove]);
  const [data, setData] = useMountedState(null);
  const [loading, setLoading] = useMountedState(true);

  const getCurrencySymbol = useCurrencySymbol();
  React.useEffect(() => {
    setLoading(true);
    Promise.all(
      [loadApplications, loadNetworks, loadPlatforms, loadCountries].map((f) =>
        f()
      )
    )
      .then((values) => {
        return [APPLICATION, NETWORK, PLATFORM, COUNTRY].reduce((m, k, i) => {
          const id = m[k];
          const label = values[i].find((opt) => opt?.id === id)?.name;
          return {
            ...m,
            [k]: label,
          };
        }, model);
      })
      .then(setData)
      .finally(() => setLoading(false));
  }, [model]);
  return (
    <View fetching={loading}>
      <Card elevation={6}>
        <CardHeader
          title={
            period ? (
              <DateRangeFormatter value={period} template={'LL'} />
            ) : (
              'AA'
            )
          }
          action={
            !isEdit && (
              <Stack direction={'row'} alignItems={'center'} spacing={1}>
                <IconButton
                  icon={<EditIcon />}
                  tooltip={'Edit'}
                  onClick={handleEdit}
                />
                <IconButton
                  icon={<CloseIcon />}
                  tooltip={'Remove'}
                  onClick={handleRemove}
                />
              </Stack>
            )
          }
        />
        <CardContent>
          <DescriptionList
            stripEmpty={true}
            data={data}
            disableGutters={true}
            termWidth={'40%'}
          >
            <DescriptionItem
              type={'status'}
              field={'status'}
              label={'Status'}
              statusFactory={Status}
            />
            <DescriptionItem field={APPLICATION} label={'Application'} />
            <DescriptionItem field={NETWORK} label={'Network'} />
            <DescriptionItem field={PLATFORM} label={'Platform'} />
            <DescriptionItem field={COUNTRY} label={'Country'} />
            <DescriptionItem
              field={'amount'}
              label={'Amount'}
              valueParser={(amount) => {
                return currency
                  ? `${getCurrencySymbol(
                      currency
                    )} ${amount} - ${currency.toUpperCase()} (Cost Adjustment)`
                  : '-';
              }}
            />
            <DescriptionItem
              field={ADJUSTMENT_REASON}
              label={'Adjustment reason'}
              valueParser={(reason) => {
                return reason === 8 ? 'Refund - FR' : null;
              }}
            />
          </DescriptionList>
        </CardContent>
      </Card>
    </View>
  );
};

export default SpendAdjustmentCard;
